import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import React, { useRef, useEffect, useCallback } from 'react';
import { useStompClient, useSubscription } from 'react-stomp-hooks';

import {
  Box,
  Stack,
  useTheme,
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useModal } from 'src/hooks/useModal';

import ChatFileInput from 'src/components/inputs/fileInput/FileInput';

import { styles } from '../styles';
import Message from './message/Message';

TicketConversation.propTypes = {
  row: PropTypes.object.isRequired,
  ticketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  statusesList: PropTypes.array,
  commentsData: PropTypes.array,
  setCommentsData: PropTypes.func,
  departmentUsers: PropTypes.array,
  hasWritePermission: PropTypes.bool,
  fetchUser: PropTypes.func.isRequired,
  onSilentRefresh: PropTypes.func,
};

export default function TicketConversation({
  row,
  ticketId,
  statusesList,
  commentsData = [],
  setCommentsData,
  departmentUsers,
  hasWritePermission,
  fetchUser,
  onSilentRefresh,
}) {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const { id: currentUserId, username } = useAuth();
  const stompClient = useStompClient();
  const scrollRef = useRef(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { register, handleSubmit, reset } = useForm({
    defaultValues: { message: '' },
  });

  const handleIncomingMessage = useCallback(
    (message) => {
      const newComment = JSON.parse(message.body);

      // Append the message to commentsData for both MESSAGE and UPDATE types
      setCommentsData((prev) => [...prev, newComment]);

      const isAnotherUser = Number(newComment.sender) !== Number(currentUserId);
      const isFileUploaded = newComment.messageType === 'FILE';

      // Always trigger a silent refresh if someone else made a change OR if a file was uploaded (to sync local ticketData.attachments)
      if ((isAnotherUser || isFileUploaded) && onSilentRefresh) {
        onSilentRefresh();
      }

      // Dispatch a dedicated signal so any open Attachments Modal updates its listing in real-time
      if (isFileUploaded) {
        window.dispatchEvent(
          new CustomEvent('attachments-updated', { detail: { ticketId } })
        );
      }
    },
    [currentUserId, onSilentRefresh, setCommentsData, ticketId]
  );

  useSubscription(`/queue/reply-${ticketId}`, handleIncomingMessage);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [commentsData, scrollToBottom]);

  const onSubmit = (data) => {
    if (data.message.trim() !== '' && stompClient) {
      const isUnassigned =
        !row.assignedTo || row.assignedTo === -1 || row.assignedTo === 0;

      // If unassigned and sender is the customer, fallback to row.createdBy to satisfy database foreign keys
      const receiver =
        currentUserId === row.createdBy
          ? isUnassigned
            ? row.createdBy
            : row.assignedTo
          : row.createdBy;

      stompClient.publish({
        destination: `/app/user-message-${ticketId}/${receiver}/${username}`,
        body: JSON.stringify({
          message: data.message,
          receiver,
          sender: currentUserId,
          messageType: 'MESSAGE',
          ticketId,
        }),
      });
      reset();
    }
  };

  const onUpload = (file) => {
    if (stompClient) {
      const isUnassigned =
        !row.assignedTo || row.assignedTo === -1 || row.assignedTo === 0;

      // If unassigned and sender is the customer, fallback to row.createdBy to satisfy database foreign keys
      const receiver =
        currentUserId === row.createdBy
          ? isUnassigned
            ? row.createdBy
            : row.assignedTo
          : row.createdBy;

      stompClient.publish({
        destination: `/app/user-message-${ticketId}/${receiver}/${username}`,
        body: JSON.stringify({
          message: file,
          receiver,
          sender: currentUserId,
          messageType: 'FILE',
          ticketId,
        }),
      });
    }
  };

  const shouldShowTimeAnchor = (index, currentItem) => {
    if (index === 0) return true;
    const prevItem = commentsData[index - 1];
    const diff = new Date(currentItem.sentAt) - new Date(prevItem.sentAt);
    return diff > 3600000;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.neutral',
      }}
    >
      <Box
        ref={scrollRef}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 3,
          px: { lg: 12, md: 6, xs: 2 },
          ...styles.scrollbarCustom,
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
            },
          },
        }}
      >
        <Stack spacing={0}>
          {commentsData.map((item, index) => {
            const timeAnchor = shouldShowTimeAnchor(index, item);

            const showSenderInfo =
              index === 0 ||
              commentsData[index - 1].sender !== item.sender ||
              commentsData[index - 1].messageType !== item.messageType ||
              timeAnchor;

            const nextItem = commentsData[index + 1];
            const isLastInGroup =
              index === commentsData.length - 1 ||
              nextItem?.sender !== item.sender ||
              nextItem?.messageType !== item.messageType ||
              shouldShowTimeAnchor(index + 1, nextItem);

            return (
              <Message
                key={item.id || index}
                item={item}
                row={row}
                showTimeAnchor={timeAnchor}
                showSenderInfo={showSenderInfo}
                isLastInGroup={isLastInGroup}
                statusesList={statusesList}
                departmentUsers={departmentUsers}
                fetchUser={fetchUser}
              />
            );
          })}
        </Stack>
      </Box>

      {hasWritePermission && (
        <Box
          sx={{
            p: 4,
            bgcolor: 'transparent',
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 2,
            mt: -2,
          }}
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{ width: '100%', maxWidth: '1100px' }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder={t('pages.ticketDetails.addMessage')}
              disabled={!hasWritePermission}
              {...register('message')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(onSubmit)();
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                  borderRadius: '12px',
                  pr: 1.5,
                  pl: 2.5,
                  border: '1.5px solid',
                  borderColor: 'divider',
                  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.03)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: 'text.secondary',
                  },
                  '&.Mui-focused': {
                    borderColor: '#00A76F',
                    boxShadow: '0px 4px 12px rgba(0, 167, 111, 0.08)',
                  },
                  '& fieldset': { border: 'none' },
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.95rem',
                  py: 1,
                  color: 'text.primary',
                  '&::placeholder': { color: '#919EAB', opacity: 0.8 },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        showModal({
                          title: t('inputs.file.fileInput'),
                          Component: ChatFileInput,
                          props: { hideModal, onUpload, ticketId: row.id },
                        })
                      }
                      sx={{
                        p: 1,
                        transition: 'transform 0.2s',
                        '&:hover': {
                          bgcolor: isDark
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.04)',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src="/assets/icons/Tickets/attachment.svg"
                        sx={{
                          width: 24,
                          height: 24,
                          filter: isDark
                            ? 'brightness(0) invert(0.85)'
                            : 'brightness(0) opacity(0.7)',
                          transition: 'filter 0.2s ease-in-out',
                        }}
                      />
                    </IconButton>

                    <IconButton
                      type="submit"
                      disabled={!hasWritePermission}
                      sx={{
                        p: 1,
                        width: '38px',
                        height: '38px',
                        bgcolor: '#00A76F',
                        borderRadius: '8px',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          bgcolor: '#008F5D',
                          transform: 'scale(1.03)',
                        },
                        '&:active': { transform: 'scale(0.95)' },
                      }}
                    >
                      <Box
                        component="img"
                        src="/assets/icons/Tickets/send.svg"
                        sx={{
                          width: 20,
                          height: 20,
                          filter: 'brightness(0) invert(1)',
                          transition: 'all 0.2s ease',
                        }}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </form>
        </Box>
      )}
    </Box>
  );
}
